import React, { useEffect, useState } from "react";
import { Buildings, ArrowsClockwise } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function DistrictComparePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/v1/metrics/compare")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Errore benchmark")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const chartData = (data || []).map((d) => ({
    nome: d.nome.split(" - ").pop().slice(0, 18),
    fullNome: d.nome,
    M1a: d.arera.m1a_pct,
    M1b: d.arera.m1b_m3_km_gg,
    alerts: d.alerts_count,
    km: d.lunghezza_rete_km,
    isCurrent: d.is_current,
  }));

  return (
    <div className="bg-[#111827] border border-slate-800 rounded-lg" data-testid="district-compare-panel">
      <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Buildings size={20} weight="duotone" className="text-[#3EB6F0]" />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Benchmark Interno</div>
            <div className="font-heading text-lg text-white">Confronto Distretti Città</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="border-slate-700 hover:bg-slate-800 gap-2" data-testid="compare-refresh-button">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
        </Button>
      </div>

      <div className="p-4">
        {!data ? (
          <div className="text-xs font-mono text-slate-500 text-center py-6">Caricamento benchmark...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              {data.map((d) => (
                <div key={d.nome} className={`border rounded-md p-3 ${d.is_current ? "border-[#3EB6F0] bg-[#3EB6F0]/5" : "border-slate-800 bg-[#0a0f1c]"}`} data-testid={`compare-card-${d.nome}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-xs font-mono font-semibold text-slate-200 truncate">{d.nome}</div>
                    {d.is_current && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#3EB6F0]/20 text-[#3EB6F0] uppercase tracking-widest">Corrente</span>}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 mb-2">{d.gestore} · {d.lunghezza_rete_km} km</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-[9px] font-mono uppercase text-slate-500">M1a</div>
                      <div className="font-mono text-lg font-semibold" style={{ color: d.arera.m1a_pct > 30 ? "#EF4444" : d.arera.m1a_pct > 20 ? "#F59E0B" : "#10B981" }}>
                        {d.arera.m1a_pct}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase text-slate-500">M1b</div>
                      <div className="font-mono text-lg font-semibold text-slate-200">{d.arera.m1b_m3_km_gg}</div>
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase text-slate-500">Allarmi</div>
                      <div className="font-mono text-lg font-semibold text-slate-200">{d.alerts_count}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 6 }}>
                  <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                  <XAxis dataKey="nome" tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickLine={{ stroke: "#334155" }} axisLine={{ stroke: "#334155" }} />
                  <YAxis yAxisId="left" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickLine={{ stroke: "#334155" }} axisLine={{ stroke: "#334155" }} label={{ value: "M1a %", angle: -90, position: "insideLeft", fill: "#64748B", fontSize: 9 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickLine={{ stroke: "#334155" }} axisLine={{ stroke: "#334155" }} label={{ value: "M1b m³/km/gg", angle: 90, position: "insideRight", fill: "#64748B", fontSize: 9 }} />
                  <Tooltip
                    contentStyle={{ background: "#0a0f1c", border: "1px solid #1E293B", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                    formatter={(v, name) => [name === "M1a" ? `${v} %` : `${v}`, name]}
                    labelFormatter={(l, p) => p?.[0]?.payload?.fullNome || l}
                  />
                  <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, color: "#94A3B8" }} />
                  <Bar yAxisId="left" dataKey="M1a" name="M1a %" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.M1a > 30 ? "#EF4444" : entry.M1a > 20 ? "#F59E0B" : "#10B981"} />
                    ))}
                  </Bar>
                  <Bar yAxisId="right" dataKey="M1b" name="M1b m³/km/gg" fill="#3EB6F0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
